/* ============================================================================
 * V4 BRAND TOKENS — cinematic rebrand (2026-04-20)
 * See docs/design/v4-landing-hero-spec.md and UI_UX_STANDARDS.md §16.
 *
 * The homepage hero leads with the understood category: AI receptionist.
 * The older V4 rotating-pillar tokens remain exported for supporting pages
 * and tests that still validate the original animation math.
 *
 * V4 tokens are ADDITIVE — v1 exports above remain so /always-open +
 * /always-answers and the marketing agents continue to resolve.
 * ========================================================================== */

export interface V4PillarPalette {
  readonly glow: string;   // light tint, text-shadow + soft glow
  readonly base: string;   // main brand color
  readonly deep: string;   // shadow / dark rim
}

export interface V4PillarDef {
  readonly key: 'on' | 'answering' | 'growing';
  readonly rotatingWord: string;        // "On.", "Answering.", "Growing."
  readonly label: string;               // "Always On", etc.
  readonly palette: V4PillarPalette;
  readonly accentHex: string;           // alias for .palette.base (compat with v1 PillarDef)
}

export const V4_PILLAR_ALWAYS_ON: V4PillarDef = {
  key: 'on',
  rotatingWord: 'On.',
  label: 'Always On',
  palette: { glow: '#7dfaff', base: '#22d3ee', deep: '#0e7b8c' },
  accentHex: '#22d3ee',
};

export const V4_PILLAR_ALWAYS_ANSWERING: V4PillarDef = {
  key: 'answering',
  rotatingWord: 'Answering.',
  label: 'Always Answering',
  palette: { glow: '#ffd592', base: '#f5a524', deep: '#8a5412' },
  accentHex: '#f5a524',
};

export const V4_PILLAR_ALWAYS_GROWING: V4PillarDef = {
  key: 'growing',
  rotatingWord: 'Growing.',
  label: 'Always Growing',
  palette: { glow: '#b9f6c0', base: '#34d399', deep: '#0f6a4a' },
  accentHex: '#34d399',
};

export const V4_PILLARS = [
  V4_PILLAR_ALWAYS_ON,
  V4_PILLAR_ALWAYS_ANSWERING,
  V4_PILLAR_ALWAYS_GROWING,
] as const;

/**
 * The full canonical V4 brand sentence — emitted via aria-label on the
 * rotating <h1> so screen readers hear it once instead of the cycling spans.
 */
export const V4_BRAND_SENTENCE =
  'Your AI receptionist answers every call, 24/7/365 — so you never lose another customer.';

/**
 * The static portion of the V4 rotating headline. The rotating word cycles
 * through V4_PILLARS[i].rotatingWord after this text.
 */
export const V4_ROTATING_HEADLINE_PREFIX = 'Your Business, Always';

export const V4_BRAND = {
  sentence: V4_BRAND_SENTENCE,
  headlinePrefix: V4_ROTATING_HEADLINE_PREFIX,
  /** Lede — keep punchy, set the promise. */
  homepageSub:
    'Bri, your AI receptionist, answers missed calls, qualifies callers, and texts you the next step before the lead goes cold.',
  /** First body paragraph — primary value proposition. */
  homepageDual:
    'Start with AI Receptionist for $99/mo. Manual setup is waived and white-glove onboarding is included, so forwarding your number is the only hard part.',
  /** Below-fold AI-grid discovery angle. Keep Business Page out of the hero. */
  homepageAiGrid:
    'Plus a smart business page below the fold that helps new customers find you in ChatGPT, Gemini, Claude, and Perplexity.',
  fabLabel: 'Talk to Bri',
  /** Kept as an identifier for aria-labels; UI no longer renders sub copy. */
  fabSubLabel: 'Live AI concierge',
  /** Hero primary CTA label — the spark-pill that opens the Talk-to-Bri modal. */
  primaryCta: 'Talk to Bri',
  primaryCtaHref: '/auth/register',
  /** Hero secondary CTA — quiet underlined link past the FAB, straight to registration. */
  heroSecondaryCta: 'Get started without the demo →',
  /** Caveat-script caption above the hero FAB. */
  heroCtaCaption: 'Start here — 60 seconds ↓',
  /**
   * Post-demo-call CTA — every FloatingVoiceV1 post-call card that doesn't
   * already carry its own plan+utm attribution (e.g. /request-demo, /x)
   * routes through this single href so plan + voice_demo attribution
   * survives from "call ended" to signup. See Task A2.
   */
  postDemoCtaHref:
    '/auth/register?plan=spark&utm_source=voice_demo&utm_medium=site&utm_campaign=post_call',
  voiceFallbackHref: 'tel:+16198483370',
} as const;

/** V4 rotating loop timing — 9 seconds total, 3 seconds per word. */
export const V4_ROTATION_INTERVAL_MS = 9_000;
export const V4_ROTATION_WORDS = V4_PILLARS.length; // 3

/**
 * Pure helper — computes which rotating word should be visible at time `t`.
 * Used by RotatingHero + any unit test that validates rotation math.
 * Returns 0, 1, or 2 matching V4_PILLARS indexing.
 */
export function v4RotationIndex(t: number): number {
  const step = V4_ROTATION_INTERVAL_MS / V4_ROTATION_WORDS; // 3000ms
  return Math.floor((t % V4_ROTATION_INTERVAL_MS) / step);
}
